import React, { useState, useEffect, useCallback } from "react";
import { savingsAPI, liabilityAccountsAPI } from "../lib/api";
import { CURRENCIES, formatCurrency, cn } from "../lib/utils";
import { toast } from "sonner";
import {
  Landmark,
  Plus,
  Pencil,
  Trash2,
  Wallet,
  TrendingUp,
  Building2,
  RefreshCw,
  AlertCircle,
  SlidersHorizontal,
  ReceiptText,
  Shield,
  Zap,
  CreditCard,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";

const ACCOUNT_TYPES = [
  "Savings",
  "Checking",
  "Digital Wallet",
  "Cash",
  "Emergency Fund",
  "Investment Buffer",
  "Other",
];

const ACCOUNT_ROLES = [
  { value: "operating", label: "Operating" },
  { value: "bills", label: "Bills" },
  { value: "savings", label: "Savings" },
  { value: "emergency", label: "Emergency" },
  { value: "reserve", label: "Reserve" },
  { value: "investment", label: "Investment" },
  { value: "wallet", label: "Wallet" },
];

const CHART_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#f59e0b",
  "#10b981", "#3b82f6", "#f97316", "#14b8a6",
];

const SAVINGS_ACCT_TYPES = new Set(["Savings", "Emergency Fund", "Investment Buffer"]);
const CASHFLOW_EXCL_ROLES = new Set(["savings", "emergency", "reserve", "investment"]);

const LIABILITY_TYPES = [
  { value: "credit_card", label: "Credit Card" },
  { value: "loan", label: "Loan" },
];

const EMPTY_LIABILITY_FORM = {
  name: "",
  type: "credit_card",
  institution: "",
  currency: "PHP",
  credit_limit: "",
  notes: "",
  is_active: true,
};

function deriveDefaults(accountType, accountRole) {
  const role = accountRole || (SAVINGS_ACCT_TYPES.has(accountType) ? "savings" : "operating");
  const isSavings = SAVINGS_ACCT_TYPES.has(accountType) || ["savings", "emergency", "reserve"].includes(role);
  const inCashflow = !CASHFLOW_EXCL_ROLES.has(role);
  return { role, isSavings, inCashflow };
}

const EMPTY_FORM = {
  bank_name: "",
  account_label: "",
  amount: "",
  currency: "PHP",
  account_type: "Savings",
  account_role: "savings",
  is_savings_account: true,
  include_in_cashflow: false,
  include_in_networth: true,
  notes: "",
  last_updated: new Date().toISOString().split("T")[0],
};

function txTypeBadge(type) {
  if (type === "inflow") return "bg-green-100 text-green-700";
  if (type === "outflow") return "bg-red-100 text-red-700";
  if (type === "opening_balance") return "bg-blue-100 text-blue-700";
  return "bg-gray-100 text-gray-700";
}

export default function SavingsTrackerPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ new_balance: "", notes: "" });
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [txAccount, setTxAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("cash");

  // Liability accounts state
  const [liabilityAccounts, setLiabilityAccounts] = useState([]);
  const [liabilityLoading, setLiabilityLoading] = useState(false);
  const [liabilityLoaded, setLiabilityLoaded] = useState(false);
  const [liabilityDialogOpen, setLiabilityDialogOpen] = useState(false);
  const [liabilityEditing, setLiabilityEditing] = useState(null);
  const [liabilityForm, setLiabilityForm] = useState(EMPTY_LIABILITY_FORM);
  const [liabilitySaving, setLiabilitySaving] = useState(false);
  const [liabilityDeleteConfirm, setLiabilityDeleteConfirm] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await savingsAPI.list();
      setEntries(res.data || []);
    } catch (err) {
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = (entry) => {
    setEditing(entry);
    const { role, isSavings, inCashflow } = deriveDefaults(entry.account_type || "Savings", entry.account_role);
    setForm({
      bank_name: entry.bank_name || "",
      account_label: entry.account_label || "",
      amount: entry.amount?.toString() || "",
      currency: entry.currency || "PHP",
      account_type: entry.account_type || "Savings",
      account_role: entry.account_role || role,
      is_savings_account: entry.is_savings_account != null ? entry.is_savings_account : isSavings,
      include_in_cashflow: entry.include_in_cashflow != null ? entry.include_in_cashflow : inCashflow,
      include_in_networth: entry.include_in_networth != null ? entry.include_in_networth : true,
      notes: entry.notes || "",
      last_updated: entry.last_updated || new Date().toISOString().split("T")[0],
    });
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setEditing(null); };

  const handleTypeChange = (newType) => {
    const { role, isSavings, inCashflow } = deriveDefaults(newType, null);
    setForm((f) => ({ ...f, account_type: newType, account_role: role, is_savings_account: isSavings, include_in_cashflow: inCashflow }));
  };

  const handleRoleChange = (newRole) => {
    const { isSavings, inCashflow } = deriveDefaults(form.account_type, newRole);
    setForm((f) => ({ ...f, account_role: newRole, is_savings_account: isSavings, include_in_cashflow: inCashflow }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bank_name.trim()) { toast.error("Bank / Institution Name is required"); return; }
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt < 0) { toast.error("Please enter a valid amount"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: amt };
      if (editing) { await savingsAPI.update(editing.id, payload); toast.success("Account updated"); }
      else { await savingsAPI.create(payload); toast.success("Account added"); }
      closeDialog(); fetchEntries();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save account");
    } finally { setSaving(false); }
  };

  const handleDelete = async (entry) => {
    try {
      await savingsAPI.delete(entry.id);
      toast.success("Account deleted"); setDeleteConfirm(null); fetchEntries();
    } catch (err) { toast.error("Failed to delete account"); }
  };

  const openAdjust = (entry) => {
    setAdjustTarget(entry);
    setAdjustForm({ new_balance: String(entry.current_balance ?? entry.amount ?? ""), notes: "" });
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    const newBal = parseFloat(adjustForm.new_balance);
    if (isNaN(newBal)) { toast.error("Please enter a valid balance"); return; }
    setAdjustSaving(true);
    try {
      await savingsAPI.adjustBalance(adjustTarget.id, { new_balance: newBal, notes: adjustForm.notes || null });
      toast.success("Balance adjusted and logged"); setAdjustTarget(null); fetchEntries();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to adjust balance");
    } finally { setAdjustSaving(false); }
  };

  const openTransactions = async (entry) => {
    setTxAccount(entry); setTxLoading(true);
    try {
      const res = await savingsAPI.getTransactions(entry.id);
      setTransactions(res.data || []);
    } catch { toast.error("Failed to load transactions"); }
    finally { setTxLoading(false); }
  };

  // ── Liability accounts handlers ────────────────────────────────────────────

  const fetchLiabilityAccounts = useCallback(async () => {
    setLiabilityLoading(true);
    try {
      const res = await liabilityAccountsAPI.list();
      setLiabilityAccounts(res.data || []);
      setLiabilityLoaded(true);
    } catch { toast.error("Failed to load liability accounts"); }
    finally { setLiabilityLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === "liability" && !liabilityLoaded && !liabilityLoading) {
      fetchLiabilityAccounts();
    }
  }, [activeTab, liabilityLoaded, liabilityLoading, fetchLiabilityAccounts]);

  const openLiabilityCreate = () => {
    setLiabilityEditing(null);
    setLiabilityForm(EMPTY_LIABILITY_FORM);
    setLiabilityDialogOpen(true);
  };

  const openLiabilityEdit = (account) => {
    setLiabilityEditing(account);
    setLiabilityForm({
      name: account.name || "",
      type: account.type || "credit_card",
      institution: account.institution || "",
      currency: account.currency || "PHP",
      credit_limit: account.credit_limit != null ? String(account.credit_limit) : "",
      notes: account.notes || "",
      is_active: account.is_active !== false,
    });
    setLiabilityDialogOpen(true);
  };

  const closeLiabilityDialog = () => { setLiabilityDialogOpen(false); setLiabilityEditing(null); };

  const handleLiabilitySubmit = async (e) => {
    e.preventDefault();
    if (!liabilityForm.name.trim()) { toast.error("Account name is required"); return; }
    if (!liabilityForm.type) { toast.error("Account type is required"); return; }
    setLiabilitySaving(true);
    try {
      const payload = {
        ...liabilityForm,
        credit_limit: liabilityForm.credit_limit !== "" ? parseFloat(liabilityForm.credit_limit) : null,
      };
      if (liabilityEditing) {
        await liabilityAccountsAPI.update(liabilityEditing.id, payload);
        toast.success("Liability account updated");
      } else {
        await liabilityAccountsAPI.create(payload);
        toast.success("Liability account added");
      }
      closeLiabilityDialog();
      fetchLiabilityAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save liability account");
    } finally { setLiabilitySaving(false); }
  };

  const handleLiabilityDelete = async (account) => {
    try {
      await liabilityAccountsAPI.delete(account.id);
      toast.success("Liability account deleted");
      setLiabilityDeleteConfirm(null);
      fetchLiabilityAccounts();
    } catch { toast.error("Failed to delete liability account"); }
  };

  const primaryCurrency = entries.length > 0 ? (entries[0].currency || "PHP") : "PHP";
  const totalCash = entries.reduce((s, e) => s + (e.current_balance ?? e.amount ?? 0), 0);
  const spendableCash = entries.filter((e) => e.include_in_cashflow).reduce((s, e) => s + (e.current_balance ?? e.amount ?? 0), 0);
  const protectedSavings = entries.filter((e) => e.is_savings_account || ["savings","emergency","reserve"].includes(e.account_role)).reduce((s, e) => s + (e.current_balance ?? e.amount ?? 0), 0);
  const networthBalance = entries.filter((e) => e.include_in_networth !== false).reduce((s, e) => s + (e.current_balance ?? e.amount ?? 0), 0);
  const recentEntry = entries.reduce((latest, e) => !latest ? e : ((e.last_updated || e.updated_at || "") > (latest.last_updated || latest.updated_at || "") ? e : latest), null);
  const chartData = entries.filter((e) => e.currency === primaryCurrency).sort((a, b) => (b.current_balance ?? b.amount ?? 0) - (a.current_balance ?? a.amount ?? 0)).slice(0, 8).map((e) => ({ name: e.account_label || e.bank_name, value: e.current_balance ?? e.amount ?? 0 }));

  const currentAdjBal = adjustTarget ? (adjustTarget.current_balance ?? adjustTarget.amount ?? 0) : 0;
  const newAdjBal = adjustForm.new_balance !== "" ? parseFloat(adjustForm.new_balance) : NaN;
  const adjDiff = isNaN(newAdjBal) ? null : newAdjBal - currentAdjBal;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Landmark className="h-6 w-6 text-primary" />
            Accounts &amp; Balances
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Track your real balances across banks, wallets, and cash accounts.</p>
        </div>
        {activeTab === "cash" ? (
          <Button onClick={openCreate} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />Add Account
          </Button>
        ) : (
          <Button onClick={openLiabilityCreate} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />Add Liability Account
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="cash" className="flex items-center gap-2">
            <Landmark className="h-4 w-4" />Cash Accounts
          </TabsTrigger>
          <TabsTrigger value="liability" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />Liability Accounts
          </TabsTrigger>
        </TabsList>

        {/* ── Cash Accounts Tab ─────────────────────────────────────────── */}
        <TabsContent value="cash" className="space-y-6 mt-6">

      {entries.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[
            { label: "Total Cash", value: totalCash, cls: "text-primary" },
            { label: "Spendable", value: spendableCash, cls: "text-emerald-500", icon: <Zap className="h-3 w-3 text-emerald-500" /> },
            { label: "Protected", value: protectedSavings, cls: "text-violet-500", icon: <Shield className="h-3 w-3 text-violet-500" /> },
            { label: "Net Worth", value: networthBalance, cls: "text-blue-500" },
          ].map(({ label, value, cls, icon }) => (
            <Card key={label} className="border-border">
              <CardHeader className="pb-2">
                <CardDescription className="text-xs font-medium uppercase tracking-wide flex items-center gap-1">
                  {icon}{label}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className={`text-xl font-bold truncate ${cls}`}>{formatCurrency(value, primaryCurrency)}</p>
              </CardContent>
            </Card>
          ))}
          <Card className="border-border">
            <CardHeader className="pb-2"><CardDescription className="text-xs font-medium uppercase tracking-wide">Accounts</CardDescription></CardHeader>
            <CardContent className="flex items-center gap-2"><Wallet className="h-6 w-6 text-muted-foreground" /><p className="text-2xl font-bold">{entries.length}</p></CardContent>
          </Card>
          {recentEntry && (
            <Card className="border-border">
              <CardHeader className="pb-2"><CardDescription className="text-xs font-medium uppercase tracking-wide">Recently Updated</CardDescription></CardHeader>
              <CardContent>
                <p className="text-sm font-semibold truncate">{recentEntry.account_label || recentEntry.bank_name}</p>
                <p className="text-xs text-muted-foreground mt-1">{recentEntry.last_updated || "—"}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {chartData.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />Balance Distribution ({primaryCurrency})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={chartData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                    {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v, primaryCurrency)} />
                  <Legend iconType="circle" iconSize={8} formatter={(v) => <span className="text-xs">{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">All Accounts</h2>
          <Button variant="ghost" size="sm" onClick={fetchEntries} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />Refresh
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((n) => (
              <Card key={n} className="animate-pulse"><CardContent className="p-4 space-y-3">
                <div className="h-4 bg-muted rounded w-2/3" /><div className="h-6 bg-muted rounded w-1/2" /><div className="h-3 bg-muted rounded w-full" />
              </CardContent></Card>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-4">
              <Landmark className="h-12 w-12 text-muted-foreground/40" />
              <div>
                <p className="text-base font-medium text-muted-foreground">No accounts yet</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Start tracking your real bank accounts, wallets, and cash here.</p>
              </div>
              <Button onClick={openCreate} size="sm"><Plus className="h-4 w-4 mr-2" />Add Your First Account</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Bank / Wallet</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Label</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Role</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Balance</th>
                    <th className="text-center px-4 py-3 font-medium text-muted-foreground" title="Protected Savings"><Shield className="h-3.5 w-3.5 mx-auto text-violet-500" /></th>
                    <th className="text-center px-4 py-3 font-medium text-muted-foreground" title="Spendable Cash"><Zap className="h-3.5 w-3.5 mx-auto text-emerald-500" /></th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Updated</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="truncate max-w-[140px]">{entry.bank_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground truncate max-w-[120px]">{entry.account_label || "—"}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="text-xs whitespace-nowrap">{entry.account_type || "Savings"}</Badge></td>
                      <td className="px-4 py-3"><Badge variant="secondary" className="text-xs whitespace-nowrap capitalize">{entry.account_role || "—"}</Badge></td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-500 whitespace-nowrap">{formatCurrency(entry.current_balance ?? entry.amount ?? 0, entry.currency)}</td>
                      <td className="px-4 py-3 text-center">{entry.is_savings_account ? <Shield className="h-4 w-4 text-violet-500 mx-auto" /> : <span className="text-muted-foreground/30">—</span>}</td>
                      <td className="px-4 py-3 text-center">{entry.include_in_cashflow ? <Zap className="h-4 w-4 text-emerald-500 mx-auto" /> : <span className="text-muted-foreground/30">—</span>}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{entry.last_updated || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(entry)} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openAdjust(entry)} title="Adjust Balance"><SlidersHorizontal className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openTransactions(entry)} title="View Transactions"><ReceiptText className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleteConfirm(entry)} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 gap-3 md:hidden">
              {entries.map((entry) => (
                <Card key={entry.id} className="border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{entry.bank_name}</p>
                        {entry.account_label && <p className="text-sm text-muted-foreground truncate">{entry.account_label}</p>}
                      </div>
                      <p className="font-bold text-emerald-500 whitespace-nowrap text-sm">{formatCurrency(entry.current_balance ?? entry.amount ?? 0, entry.currency)}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">{entry.account_type || "Savings"}</Badge>
                      <Badge variant="secondary" className="text-xs capitalize">{entry.account_role || "—"}</Badge>
                      {entry.is_savings_account && <Badge variant="outline" className="text-xs text-violet-600 border-violet-500/30"><Shield className="h-2.5 w-2.5 mr-1" />Protected</Badge>}
                      {entry.include_in_cashflow && <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-500/30"><Zap className="h-2.5 w-2.5 mr-1" />Spendable</Badge>}
                      {entry.last_updated && <span className="text-xs text-muted-foreground">{entry.last_updated}</span>}
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => openEdit(entry)}><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => openAdjust(entry)}><SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />Adjust</Button>
                      <Button variant="outline" size="sm" onClick={() => openTransactions(entry)}><ReceiptText className="h-3.5 w-3.5 mr-1.5" />Transactions</Button>
                      <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setDeleteConfirm(entry)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

        </TabsContent>

        {/* ── Liability Accounts Tab ─────────────────────────────────────── */}
        <TabsContent value="liability" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Liability Accounts</h2>
                <p className="text-sm text-muted-foreground">Credit cards and loans that carry installment obligations.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={fetchLiabilityAccounts} disabled={liabilityLoading}>
                <RefreshCw className={cn("h-4 w-4 mr-1.5", liabilityLoading && "animate-spin")} />Refresh
              </Button>
            </div>

            {liabilityLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((n) => (
                  <Card key={n} className="animate-pulse"><CardContent className="p-4 space-y-3">
                    <div className="h-4 bg-muted rounded w-2/3" /><div className="h-6 bg-muted rounded w-1/2" /><div className="h-3 bg-muted rounded w-full" />
                  </CardContent></Card>
                ))}
              </div>
            ) : liabilityAccounts.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-4">
                  <CreditCard className="h-12 w-12 text-muted-foreground/40" />
                  <div>
                    <p className="text-base font-medium text-muted-foreground">No liability accounts yet</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Add your credit cards and loans to track installment obligations.</p>
                  </div>
                  <Button onClick={openLiabilityCreate} size="sm"><Plus className="h-4 w-4 mr-2" />Add Your First Liability Account</Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Name</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Institution</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Currency</th>
                        <th className="text-right px-4 py-3 font-medium text-muted-foreground">Credit Limit</th>
                        <th className="text-center px-4 py-3 font-medium text-muted-foreground">Status</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {liabilityAccounts.map((account) => (
                        <tr key={account.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-medium">
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="truncate max-w-[160px]">{account.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs whitespace-nowrap capitalize">
                              {account.type?.replace("_", " ")}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{account.institution || "—"}</td>
                          <td className="px-4 py-3 text-muted-foreground">{account.currency}</td>
                          <td className="px-4 py-3 text-right text-muted-foreground">
                            {account.credit_limit != null ? formatCurrency(account.credit_limit, account.currency) : "—"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant={account.is_active !== false ? "default" : "secondary"} className="text-xs">
                              {account.is_active !== false ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 justify-end">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openLiabilityEdit(account)} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setLiabilityDeleteConfirm(account)} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="grid grid-cols-1 gap-3 md:hidden">
                  {liabilityAccounts.map((account) => (
                    <Card key={account.id} className="border-border">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold truncate">{account.name}</p>
                            {account.institution && <p className="text-sm text-muted-foreground">{account.institution}</p>}
                          </div>
                          <Badge variant={account.is_active !== false ? "default" : "secondary"} className="text-xs shrink-0">
                            {account.is_active !== false ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge variant="outline" className="text-xs capitalize">{account.type?.replace("_", " ")}</Badge>
                          <span className="text-xs text-muted-foreground">{account.currency}</span>
                          {account.credit_limit != null && (
                            <span className="text-xs text-muted-foreground">Limit: {formatCurrency(account.credit_limit, account.currency)}</span>
                          )}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button variant="outline" size="sm" onClick={() => openLiabilityEdit(account)}><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit</Button>
                          <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setLiabilityDeleteConfirm(account)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add / Edit Cash Account Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-full">
          <DialogHeader><DialogTitle>{editing ? "Edit Cash Account" : "Add Cash Account"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="bank_name">Bank / Institution Name <span className="text-destructive">*</span></Label>
              <Input id="bank_name" placeholder="e.g. BDO, GCash, PayPal" value={form.bank_name} onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account_label">Account Label / Nickname</Label>
              <Input id="account_label" placeholder="e.g. Emergency Fund, Tax Fund" value={form.account_label} onChange={(e) => setForm((f) => ({ ...f, account_label: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="amount">Current Balance <span className="text-destructive">*</span></Label>
                <Input id="amount" type="number" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.symbol}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Account Type</Label>
                <Select value={form.account_type} onValueChange={handleTypeChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Account Role</Label>
                <Select value={form.account_role} onValueChange={handleRoleChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCOUNT_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-md border border-border p-3 bg-muted/30">
              <div className="flex items-center gap-2">
                <Switch id="is_savings" checked={!!form.is_savings_account} onCheckedChange={(v) => setForm((f) => ({ ...f, is_savings_account: v }))} />
                <Label htmlFor="is_savings" className="text-sm cursor-pointer"><Shield className="h-3 w-3 inline mr-1 text-violet-500" />Protected</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="in_cashflow" checked={!!form.include_in_cashflow} onCheckedChange={(v) => setForm((f) => ({ ...f, include_in_cashflow: v }))} />
                <Label htmlFor="in_cashflow" className="text-sm cursor-pointer"><Zap className="h-3 w-3 inline mr-1 text-emerald-500" />Spendable</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="in_networth" checked={!!form.include_in_networth} onCheckedChange={(v) => setForm((f) => ({ ...f, include_in_networth: v }))} />
                <Label htmlFor="in_networth" className="text-sm cursor-pointer">Net Worth</Label>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_updated">Last Updated Date</Label>
              <Input id="last_updated" type="date" value={form.last_updated} onChange={(e) => setForm((f) => ({ ...f, last_updated: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <textarea id="notes" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none" rows={3} placeholder="Optional notes..." value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeDialog} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? "Saving..." : editing ? "Save Changes" : "Add Account"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust Balance Dialog */}
      <Dialog open={!!adjustTarget} onOpenChange={(open) => !open && setAdjustTarget(null)}>
        <DialogContent className="sm:max-w-sm w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="h-5 w-5" />Adjust Balance</DialogTitle>
          </DialogHeader>
          {adjustTarget && (
            <form onSubmit={handleAdjust} className="space-y-4">
              <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1">
                <p className="font-medium">{adjustTarget.bank_name}</p>
                {adjustTarget.account_label && <p className="text-muted-foreground">{adjustTarget.account_label}</p>}
                <p className="text-muted-foreground">Current balance: <strong className="text-foreground">{formatCurrency(currentAdjBal, adjustTarget.currency)}</strong></p>
              </div>
              {adjDiff !== null && (
                <div className="rounded-md bg-muted/30 p-3 text-sm border border-border">
                  <p className="text-muted-foreground">New balance: <strong className="text-foreground">{formatCurrency(newAdjBal, adjustTarget.currency)}</strong></p>
                  <p className={`text-xs font-medium mt-1 ${adjDiff >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    Difference: {adjDiff >= 0 ? "+" : ""}{formatCurrency(adjDiff, adjustTarget.currency)}
                  </p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>New Actual Balance</Label>
                <Input type="number" min="0" step="0.01" placeholder="Enter actual balance" value={adjustForm.new_balance} onChange={(e) => setAdjustForm((f) => ({ ...f, new_balance: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label>Notes (optional)</Label>
                <Input placeholder="Reason for adjustment..." value={adjustForm.notes} onChange={(e) => setAdjustForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setAdjustTarget(null)} disabled={adjustSaving}>Cancel</Button>
                <Button type="submit" disabled={adjustSaving}>{adjustSaving ? "Saving..." : "Apply Adjustment"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Transaction Log Dialog */}
      <Dialog open={!!txAccount} onOpenChange={(open) => !open && setTxAccount(null)}>
        <DialogContent className="sm:max-w-xl w-full max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ReceiptText className="h-5 w-5" />Transaction Log
              {txAccount && <span className="font-normal text-muted-foreground text-sm">— {txAccount.account_label || txAccount.bank_name}</span>}
            </DialogTitle>
          </DialogHeader>
          {txLoading ? (
            <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full" /></div>
          ) : transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No transactions yet for this account.</p>
          ) : (
            <div className="space-y-2">
              {transactions.map((tx) => (
                <div key={tx.id} className="rounded-md border border-border p-3 text-sm space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${txTypeBadge(tx.type)}`}>{tx.type.replace(/_/g, " ")}</span>
                      <span className="text-xs text-muted-foreground capitalize">{tx.source?.replace(/_/g, " ")}</span>
                    </div>
                    <span className={`font-mono font-semibold text-sm ${tx.type === "inflow" || tx.type === "opening_balance" ? "text-emerald-600" : tx.type === "outflow" ? "text-red-500" : "text-blue-500"}`}>
                      {tx.type === "outflow" ? "-" : "+"}{formatCurrency(tx.amount, tx.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{tx.transaction_date || tx.created_at?.split("T")[0]}</span>
                    <span>Balance after: <strong className="text-foreground">{formatCurrency(tx.user_confirmed_balance_after, tx.currency)}</strong></span>
                  </div>
                  {tx.notes && <p className="text-xs text-muted-foreground">{tx.notes}</p>}
                  {tx.manual_adjustment_amount !== 0 && (
                    <p className="text-xs text-orange-500">Manual adj: {tx.manual_adjustment_amount > 0 ? "+" : ""}{formatCurrency(tx.manual_adjustment_amount, tx.currency)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertCircle className="h-5 w-5" />Delete Account?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Are you sure you want to delete <strong>{deleteConfirm?.account_label || deleteConfirm?.bank_name}</strong>? This cannot be undone.</p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Liability Account Dialog */}
      <Dialog open={liabilityDialogOpen} onOpenChange={setLiabilityDialogOpen}>
        <DialogContent className="sm:max-w-md w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {liabilityEditing ? "Edit Liability Account" : "Add Liability Account"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLiabilitySubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="liability_name">Account Name <span className="text-destructive">*</span></Label>
              <Input
                id="liability_name"
                placeholder="e.g. BPI Platinum Card, BDO Auto Loan"
                value={liabilityForm.name}
                onChange={(e) => setLiabilityForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Type <span className="text-destructive">*</span></Label>
                <Select value={liabilityForm.type} onValueChange={(v) => setLiabilityForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LIABILITY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select value={liabilityForm.currency} onValueChange={(v) => setLiabilityForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="liability_institution">Institution / Bank</Label>
              <Input
                id="liability_institution"
                placeholder="e.g. BPI, BDO, Security Bank"
                value={liabilityForm.institution}
                onChange={(e) => setLiabilityForm((f) => ({ ...f, institution: e.target.value }))}
              />
            </div>
            {liabilityForm.type === "credit_card" && (
              <div className="space-y-1.5">
                <Label htmlFor="credit_limit">Credit Limit (optional)</Label>
                <Input
                  id="credit_limit"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 100000"
                  value={liabilityForm.credit_limit}
                  onChange={(e) => setLiabilityForm((f) => ({ ...f, credit_limit: e.target.value }))}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="liability_notes">Notes (optional)</Label>
              <textarea
                id="liability_notes"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                rows={2}
                placeholder="Optional notes..."
                value={liabilityForm.notes}
                onChange={(e) => setLiabilityForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="liability_active"
                checked={!!liabilityForm.is_active}
                onCheckedChange={(v) => setLiabilityForm((f) => ({ ...f, is_active: v }))}
              />
              <Label htmlFor="liability_active" className="text-sm cursor-pointer">Active</Label>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeLiabilityDialog} disabled={liabilitySaving}>Cancel</Button>
              <Button type="submit" disabled={liabilitySaving}>
                {liabilitySaving ? "Saving..." : liabilityEditing ? "Save Changes" : "Add Account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Liability Account Confirmation */}
      <Dialog open={!!liabilityDeleteConfirm} onOpenChange={(open) => !open && setLiabilityDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertCircle className="h-5 w-5" />Delete Liability Account?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{liabilityDeleteConfirm?.name}</strong>? Existing installment schedules linked to this account will retain the reference but the account name will no longer resolve.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setLiabilityDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleLiabilityDelete(liabilityDeleteConfirm)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
